import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { useMemo, useState } from 'react'
import { type ProgramBreakdownRow } from '../../../../models/peerToPeer/Dashboard'
import { type ColSpec } from './goalViews'
import { SectionCard } from './SectionCard'
import { styles } from './ProgramBreakdownTable.styles'

const SortIcon = ({ active, ascending }: { active: boolean, ascending: boolean }): JSX.Element => {
  if (!active) return <UnfoldMoreIcon sx={{ fontSize: 14, color: 'var(--grey-500)' }} />
  return ascending ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
}

const compare = (a: ProgramBreakdownRow[keyof ProgramBreakdownRow], b: ProgramBreakdownRow[keyof ProgramBreakdownRow]): number =>
  typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))

interface Props {
  rows: ProgramBreakdownRow[]
  columns: ColSpec[]
}

export const ProgramBreakdownTable = ({ rows, columns }: Props): JSX.Element => {
  const [sortKey, setSortKey] = useState<keyof ProgramBreakdownRow>('building')
  const [ascending, setAscending] = useState(true)

  const sorted = useMemo(
    () => [...rows].sort((a, b) => ascending ? compare(a[sortKey], b[sortKey]) : -compare(a[sortKey], b[sortKey])),
    [rows, sortKey, ascending]
  )

  const sortBy = (key: keyof ProgramBreakdownRow): void => {
    if (sortKey === key) { setAscending(!ascending) } else { setSortKey(key); setAscending(true) }
  }

  return (
    <SectionCard title='Program Breakdown Details'>
      <Box sx={styles.scroller}>
        <Table size='small' data-testid='p2pProgramBreakdown'>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.label} align={column.align} onClick={() => { sortBy(column.key) }} sx={styles.headerCell}>
                  <Box component='span' sx={styles.headerLabel}>
                    {column.label}
                    <SortIcon active={sortKey === column.key} ascending={ascending} />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, index) => (
              <TableRow key={index} sx={styles.row}>
                {columns.map(column => (
                  <TableCell key={column.label} align={column.align} sx={styles.cell}>
                    {column.render != null ? column.render(row) : String(row[column.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </SectionCard>
  )
}
